import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, catchError, map, Observable, of, shareReplay, tap, throwError, timeout } from 'rxjs';
import { ConfigService } from 'src/app/common/services/config/config.service';
import {
  IAppState,
  IFrontendSettings,
} from '../ngrx.stores/frontend.settings/states';
import { frontendSettingsSelector } from '../ngrx.stores/frontend.settings/selectors';
import { ApiErrorHandler } from 'src/app/common/services/api/api.error.handler';

@Injectable({
  providedIn: 'root',
})
export class GoogleMapService {
  private readonly apiLoadedSubject = new BehaviorSubject<boolean>(false);
  private readonly apiLoadingSubject = new BehaviorSubject<boolean>(false);
  private apiLoadPromise: Promise<boolean> | null = null;
  
  apiLoaded$: Observable<boolean> = this.apiLoadedSubject.asObservable();
  apiLoading$: Observable<boolean> = this.apiLoadingSubject.asObservable();
  
  isLoaded = false;
  placesLibrary: google.maps.PlacesLibrary | null = null;
  
  ngRxStore = inject(Store<IAppState>);
  apiErrorHandler = inject(ApiErrorHandler);
  frontendSettings: IFrontendSettings | null = null;

  constructor(public httpClient: HttpClient) {
    this.initializeSettings();
  }

  private initializeSettings(): void {
    this.ngRxStore
      .pipe(select(frontendSettingsSelector))
      .subscribe((settings) => {
        if (settings) {
          this.frontendSettings = settings;
          console.log('[GoogleMapService] Frontend settings loaded');
        }
      });
  }

  loadGoogleMapApi(): Observable<boolean> {
    // Return cached result if already loaded
    if (this.isLoaded) {
      return of(true);
    }

    // Return existing promise if already loading
    if (this.apiLoadPromise) {
      return new Observable(subscriber => {
        this.apiLoadPromise!.then(
          result => {
            subscriber.next(result);
            subscriber.complete();
          },
          error => subscriber.error(error)
        );
      });
    }

    // Start loading process
    this.apiLoadPromise = this.performApiLoad();
    
    return new Observable(subscriber => {
      this.apiLoadPromise!.then(
        result => {
          subscriber.next(result);
          subscriber.complete();
        },
        error => subscriber.error(error)
      );
    });
  }

  private async performApiLoad(): Promise<boolean> {
  try {
    // Validate prerequisites
    if (!this.validatePrerequisites()) {
      return false;
    }

    this.apiLoadingSubject.next(true);
    console.log('[GoogleMapService] Starting API load...');

    // Check if API is already available
    if (window['google']?.maps?.places) {
      console.log('[GoogleMapService] API already available');
      this.onApiLoadSuccess();
      return true;
    }

    const googleKey = this.frontendSettings?.customSettings?.dataSource?.googleKey;
    if (!googleKey) {
      throw new Error('Google API key not found in settings');
    }

    // FIXED: Add loading=async parameter to prevent the warning and ByteString error
    const apiUrl = `https://maps.googleapis.com/maps/api/js?v=3&libraries=places&language=en&key=${googleKey}&callback=initMap`;
    
    const result = await this.httpClient
      .jsonp(apiUrl, 'callback')
      .pipe(
        timeout(15000), // 15 second timeout
        map(() => true),
        tap(() => this.onApiLoadSuccess()),
        catchError((error) => {
          console.error('[GoogleMapService] JSONP error:', error);
          return this.handleApiLoadError(error);
        }),
        shareReplay(1)
      )
      .toPromise();

    return result || false;

  } catch (error) {
    console.error('[GoogleMapService] API load failed:', error);
    this.handleApiLoadError(error);
    return false;
  } finally {
    this.apiLoadingSubject.next(false);
  }
}

  private validatePrerequisites(): boolean {
    if (!this.frontendSettings?.customSettings?.dataSource?.googleKey) {
      const errorMessage = 'Google API key not found in frontend settings';
      console.error(`[GoogleMapService] ${errorMessage}`);
      this.apiErrorHandler.showError(errorMessage);
      return false;
    }

    if (!this.httpClient) {
      console.error('[GoogleMapService] HttpClient not available');
      return false;
    }

    return true;
  }

  private onApiLoadSuccess(): void {
    try {
      // Verify API is properly loaded
      if (!window['google']?.maps) {
        throw new Error('Google Maps API not properly loaded');
      }

      if (!window['google'].maps.places) {
        throw new Error('Google Maps Places library not loaded');
      }

      this.placesLibrary = window['google'].maps.places;
      this.isLoaded = true;
      this.apiLoadedSubject.next(true);
      
      console.log('[GoogleMapService] API loaded successfully');
      console.log('[GoogleMapService] Places library available:', !!this.placesLibrary);

    } catch (error) {
      console.error('[GoogleMapService] API validation failed:', error);
      this.handleApiLoadError(error);
    }
  }

  private handleApiLoadError(error: any): Observable<never> {
    const errorMessage = this.getErrorMessage(error);
    console.error('[GoogleMapService] API load error:', errorMessage, error);
    
    this.isLoaded = false;
    this.placesLibrary = null;
    this.apiLoadedSubject.next(false);
    this.apiErrorHandler.showError(`Failed to load Google Maps: ${errorMessage}`);
    
    return throwError(() => new Error(errorMessage));
  }

  private getErrorMessage(error: any): string {
    if (error?.name === 'TimeoutError') {
      return 'Request timed out. Please check your internet connection.';
    }
    
    if (error?.status === 0) {
      return 'Network error. Please check your internet connection.';
    }
    
    if (error?.status >= 400 && error?.status < 500) {
      return 'Invalid API key or configuration.';
    }
    
    if (error?.message) {
      return error.message;
    }
    
    return 'Unknown error occurred while loading Google Maps API';
  }

  // Method to get the places library safely
  getPlacesLibrary(): google.maps.PlacesLibrary | null {
    if (!this.isLoaded || !this.placesLibrary) {
      console.warn('[GoogleMapService] Places library not available');
      return null;
    }
    return this.placesLibrary;
  }

  // Enhanced distance calculation with validation
  getDistanceFromLatLng(lat1: number, lon1: number, lat2: number, lon2: number): number {
    // Validate inputs
    if (!this.isValidCoordinate(lat1, lon1) || !this.isValidCoordinate(lat2, lon2)) {
      console.warn('[GoogleMapService] Invalid coordinates provided for distance calculation');
      return 0;
    }

    try {
      const R = 6371; // Radius of the earth in km
      const dLat = this.deg2rad(lat2 - lat1);
      const dLon = this.deg2rad(lon2 - lon1);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.deg2rad(lat1)) *
          Math.cos(this.deg2rad(lat2)) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const d = R * c; // Distance in km
      return d * 1000; // Convert to meters
    } catch (error) {
      console.error('[GoogleMapService] Error calculating distance:', error);
      return 0;
    }
  }

  private isValidCoordinate(lat: number, lng: number): boolean {
    return (
      typeof lat === 'number' && 
      typeof lng === 'number' && 
      !isNaN(lat) && 
      !isNaN(lng) &&
      lat >= -90 && 
      lat <= 90 && 
      lng >= -180 && 
      lng <= 180
    );
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  // Enhanced direction service with better error handling
  async getDirection(
    attractions: any[],
    travelMode: TravelMode,
    callback: (response: google.maps.DirectionsRoute) => void,
    errorCallback?: (error: string) => void
  ): Promise<void> {
    try {
      // Validate inputs
      if (!this.validateDirectionsInput(attractions, callback)) {
        return;
      }

      // Ensure API is ready
      if (!await this.waitForApiReady()) {
        const errorMsg = 'Google Maps API failed to load';
        this.handleDirectionsError(errorMsg, errorCallback);
        return;
      }

      const { origin, destination, waypoints } = this.processAttractions(attractions);
      
      if (!origin || !destination) {
        const errorMsg = 'Invalid origin or destination coordinates';
        this.handleDirectionsError(errorMsg, errorCallback);
        return;
      }

      await this.requestDirections(origin, destination, waypoints, travelMode, callback, errorCallback);

    } catch (error) {
      const errorMsg = `Unexpected error in getDirection: ${error instanceof Error ? error.message : 'Unknown error'}`;
      this.handleDirectionsError(errorMsg, errorCallback);
    }
  }

  private validateDirectionsInput(attractions: any[], callback: Function): boolean {
    if (!attractions || !Array.isArray(attractions) || attractions.length < 2) {
      const errorMsg = 'Invalid attractions data - need at least 2 locations';
      console.error(`[GoogleMapService] ${errorMsg}`);
      return false;
    }

    if (typeof callback !== 'function') {
      console.error('[GoogleMapService] Invalid callback function provided');
      return false;
    }

    return true;
  }

  private processAttractions(attractions: any[]): {
    origin: google.maps.LatLng | null;
    destination: google.maps.LatLng | null;
    waypoints: google.maps.DirectionsWaypoint[];
  } {
    let origin: google.maps.LatLng | null = null;
    let destination: google.maps.LatLng | null = null;
    const waypoints: google.maps.DirectionsWaypoint[] = [];

    attractions.forEach((attraction, index) => {
      const lat = attraction.item?.address?.lat || attraction.item?.lat;
      const lng = attraction.item?.address?.lng || attraction.item?.lng;
      
      if (!this.isValidCoordinate(lat, lng)) {
        console.warn(`[GoogleMapService] Invalid coordinates for attraction at index ${index}`);
        return;
      }

      const latlng = new google.maps.LatLng(lat, lng);
      
      if (index === 0) {
        origin = latlng;
      } else if (index === attractions.length - 1) {
        destination = latlng;
      } else {
        waypoints.push({ location: latlng, stopover: true });
      }
    });

    return { origin, destination, waypoints };
  }

  private async requestDirections(
    origin: google.maps.LatLng,
    destination: google.maps.LatLng,
    waypoints: google.maps.DirectionsWaypoint[],
    travelMode: TravelMode,
    callback: (response: google.maps.DirectionsRoute) => void,
    errorCallback?: (error: string) => void
  ): Promise<void> {
    const directionsService = new google.maps.DirectionsService();

    const request: google.maps.DirectionsRequest = {
      origin,
      destination,
      waypoints,
      optimizeWaypoints: false,
      travelMode: google.maps.TravelMode[travelMode],
    };

    directionsService.route(request, (response, status) => {
      if (status === google.maps.DirectionsStatus.OK && response?.routes?.[0]) {
        console.log('[GoogleMapService] Directions retrieved successfully');
        callback(response.routes[0]);
      } else {
        const errorMsg = `Directions service failed with status: ${status}`;
        console.error('[GoogleMapService] Direction Service error', response, status);
        this.handleDirectionsError(errorMsg, errorCallback);
      }
    });
  }

  private handleDirectionsError(errorMsg: string, errorCallback?: (error: string) => void): void {
    console.error(`[GoogleMapService] ${errorMsg}`);
    if (errorCallback) {
      errorCallback(errorMsg);
    }
  }

  // Enhanced API readiness check
  isApiReady(): boolean {
    return this.isLoaded && 
           !!window['google']?.maps && 
           !!window['google']?.maps?.places &&
           !!this.placesLibrary;
  }

  // Improved wait method with timeout and better error handling
  async waitForApiReady(timeoutMs: number = 10000): Promise<boolean> {
    if (this.isApiReady()) {
      return true;
    }

    // If not currently loading, start loading
    if (!this.apiLoadPromise) {
      this.loadGoogleMapApi().subscribe(); // Start loading but don't wait for this subscription
    }

    return new Promise((resolve) => {
      const startTime = Date.now();
      
      const checkInterval = setInterval(() => {
        if (this.isApiReady()) {
          clearInterval(checkInterval);
          console.log('[GoogleMapService] API ready after', Date.now() - startTime, 'ms');
          resolve(true);
          return;
        }

        if (Date.now() - startTime >= timeoutMs) {
          clearInterval(checkInterval);
          console.error('[GoogleMapService] API ready timeout after', timeoutMs, 'ms');
          resolve(false);
          return;
        }
      }, 100);
    });
  }

  // Method to reset the service state (useful for testing or error recovery)
  reset(): void {
    this.isLoaded = false;
    this.placesLibrary = null;
    this.apiLoadPromise = null;
    this.apiLoadedSubject.next(false);
    this.apiLoadingSubject.next(false);
    console.log('[GoogleMapService] Service reset');
  }

  // Health check method
  getHealthStatus(): {
    isLoaded: boolean;
    hasPlacesLibrary: boolean;
    hasSettings: boolean;
    hasApiKey: boolean;
  } {
    return {
      isLoaded: this.isLoaded,
      hasPlacesLibrary: !!this.placesLibrary,
      hasSettings: !!this.frontendSettings,
      hasApiKey: !!this.frontendSettings?.customSettings?.dataSource?.googleKey,
    };
  }
}

export enum TravelMode {
  DRIVING = 'DRIVING',
  BICYCLING = 'BICYCLING',
  WALKING = 'WALKING',
  TRANSIT = 'TRANSIT',
}